import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatMoney } from '../lib/utils';
import { ICON } from './ui';

/**
 * Verschuivingstoeslag op deze dienst (CAO art 43). Alleen zichtbaar als de roostersync
 * een wijziging kort van tevoren heeft vastgelegd.
 */
export function VerschuivingPanel({ shiftId }: { shiftId: number }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['verschuiving', shiftId], queryFn: () => api.verschuivingen.forShift(shiftId) });
  const v = data?.items[0];
  const mut = useMutation({
    mutationFn: (status: 'bevestigd' | 'afgewezen' | 'voorgesteld') => api.verschuivingen.setStatus(v!.id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['verschuiving', shiftId] });
      qc.invalidateQueries({ queryKey: ['period-summary'] });
      qc.invalidateQueries({ queryKey: ['afwijkingen'] });
    },
  });
  if (!v) return null;

  const afgewezen = v.status === 'afgewezen';
  return (
    <section className="space-y-3">
      <div className="flex items-start gap-3">
        <ArrowLeftRight className={cn(ICON.sm, 'mt-0.5 shrink-0 text-slate-400')} aria-hidden />
        <div className="min-w-0 flex-1">
          <h3>
            {v.old_start ? 'Verschoven' : 'Extra dienst'}, {v.notice_days} {v.notice_days === 1 ? 'dag' : 'dagen'} vooraf
          </h3>
          <p className="t-num mt-0.5 text-xs text-slate-400">
            {v.old_start ? `Was ${v.old_start}–${v.old_end}, werd ${v.new_start}–${v.new_end}` : `Ingeroosterd ${v.new_start}–${v.new_end}`}
            {`. ${v.pct}% toeslag over ${String(v.uren).replace('.', ',')}u (CAO art. 43).`}
          </p>
        </div>
        <div className={cn('t-num shrink-0 text-sm font-semibold', afgewezen ? 'text-slate-500 line-through' : 'text-money')}>
          {formatMoney(afgewezen ? 0 : v.bedrag)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pl-7">
        {v.status === 'bevestigd' && <span className="status-ok">Bevestigd, telt mee</span>}
        {v.status !== 'bevestigd' && (
          <button className="btn-secondary btn-sm" disabled={mut.isPending} onClick={() => mut.mutate('bevestigd')}>
            Klopt
          </button>
        )}
        {!afgewezen && (
          <button className="btn-ghost btn-sm" disabled={mut.isPending} onClick={() => mut.mutate('afgewezen')}>
            Klopt niet
          </button>
        )}
        {afgewezen && (
          <button className="btn-ghost btn-sm" disabled={mut.isPending} onClick={() => mut.mutate('voorgesteld')}>
            Toch meetellen
          </button>
        )}
      </div>
    </section>
  );
}
